import * as Yup from "yup";
import { useEffect, useRef, useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import { useLocation } from "react-router-dom";
import { Stack, Box, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import { parse as parseDomain } from "tldts";
import authentication from "../services/AmplifyServices";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import AmplifyEnv from "../constants/AmplifyEnv";

const easing = [0.6, -0.05, 0.01, 0.99];
const animate = {
  opacity: 1,
  y: 0,
  transition: { duration: 0.6, ease: easing, delay: 0.16 },
};

const SUBMIT_COOLDOWN_MS = 5000;

const COMMON_DOMAINS = ["gmail.com", "outlook.com", "hotmail.com", "yahoo.com", "icloud.com", "1receipt.io"];

const textMetrics = {
  fontFamily: "inherit",
  fontSize: "1rem",
  lineHeight: "1.4375em",
  letterSpacing: "0.15px",
  fontWeight: 400,
};

const hasValidVerification = (email) => {
  if (!email) return false;
  const trimmed = String(email).trim();
  const at = trimmed.lastIndexOf("@");
  if (at === -1 || at === trimmed.length - 1) return false;
  const info = parseDomain(trimmed.slice(at + 1).toLowerCase());
  return Boolean(info.domain && info.publicSuffix && info.isIcann);
};

const getDomainRemainder = (input) => {
  if (!input) return "";
  const at = input.lastIndexOf("@");
  if (at <= 0) return "";
  const local = input.slice(0, at);
  const domainPart = input.slice(at + 1).toLowerCase();
  if (!local || /\s/.test(local) || !domainPart) return "";
  const match = COMMON_DOMAINS.find((d) => d.startsWith(domainPart) && d !== domainPart);
  return match ? match.slice(domainPart.length) : "";
};

const SignupSchema = Yup.object().shape({
  fullName: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Name is required"),
  email: Yup.string()
    .email("Email must be a valid email address")
    .required("Email is required")
    .test("tld-domain", "Please enter a valid email", (value) => hasValidVerification(value)),
});

const useIsDark = () => {
  const [isDark, setIsDark] = useState(
    document.documentElement.getAttribute("data-theme") === "dark"
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setIsDark(el.getAttribute("data-theme") === "dark"));
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return isDark;
};

const SignupForm = () => {
  const { search } = useLocation();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isDark = useIsDark();
  const [domainRemainder, setDomainRemainder] = useState("");
  const lastSubmitRef = useRef(0);

  useEffect(() => {
    const redirectURL = new URLSearchParams(search).get("redirect");
    if (redirectURL) localStorage.setItem("redirectURL", redirectURL);
  }, [search]);

  const formik = useFormik({
    initialValues: { fullName: "", email: "" },
    validationSchema: SignupSchema,
    onSubmit: async ({ fullName, email }) => {
      const now = Date.now();
      if (now - lastSubmitRef.current < SUBMIT_COOLDOWN_MS) {
        toast.warn("Please wait a few seconds before trying again.", { toastId: "too-fast" });
        return;
      }
      lastSubmitRef.current = now;

      if (!executeRecaptcha) {
        toast.error("Security verification is not ready. Please try again.");
        return;
      }

      let recaptchaToken;
      try {
        recaptchaToken = await executeRecaptcha("signup");
        // TEMP TEST (remove after): reuse the same token to trigger timeout-or-duplicate
        // recaptchaToken = window.__lastToken || recaptchaToken;
        // window.__lastToken = recaptchaToken;
        if (!recaptchaToken) {
          toast.error("Security verification failed. Please try again.");
          return;
        }
      } catch (error) {
        console.error("reCAPTCHA v3 error:", error);
        toast.error("Unable to complete security verification. Please try again later.");
        return;
      }

      try {
        await authentication.signUp(email, fullName, recaptchaToken);
        toast("Sign up successful. Please login!");

        try {
          sessionStorage.setItem("prefillEmail", email);
          localStorage.setItem("prefillEmail", email);
        } catch {}

        // Sign-in is handled separately by the client app.
        window.location = new URLSearchParams(search).get("redirect") || "";
      } catch (err) {
        console.error("Sign up error:", err);
        const message = err?.message || "";

        if (message.includes("Email is used") || err?.name === "UsernameExistsException") {
          toast.error("Email already registered. Please sign in.", { toastId: "email-exists" });
        } else if (message.includes("RECAPTCHA_EXPIRED")) {
          toast.warn("Verification expired. Please try again.", { toastId: "recaptcha-expired" });
        } else if (message.includes("reCAPTCHA verification failed")) {
          toast.error("Something didn't look right. Please try again in a moment.", { toastId: "recaptcha-failed" });
        } else if (message.includes("RECAPTCHA_TIMEOUT") || message.includes("aborted due to timeout")) {
          toast.error("Verification timed out. Please try again.", { toastId: "timeout" });
        } else if (message.includes("EMAIL_DOMAIN_INVALID")) {
          toast.error("This email address can't receive mail. Please check it and try again.");
        } else {
          toast.error("An error occurred during sign up. Please try again.");
        }
      }
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, values, setFieldValue, submitForm } = formik;

  const acceptSuggestion = (e, shouldSubmit = false) => {
    if (!domainRemainder) return;
    e?.preventDefault?.();
    setFieldValue("email", values.email + domainRemainder, false);
    setDomainRemainder("");
    if (shouldSubmit) setTimeout(() => submitForm(), 0);
  };

  const handleEmailChange = (e) => {
    const v = e.target.value;
    setFieldValue("email", v, true);
    setDomainRemainder(getDomainRemainder(v));
  };

  const handleEmailKeyDown = (e) => {
    if (!domainRemainder) return;
    if (["Enter", "Tab", "ArrowRight"].includes(e.key)) {
      acceptSuggestion(e, e.key === "Enter");
    }
  };

  const inputStyle = {
    borderRadius: "25px",
    backgroundColor: isDark ? "#696969" : "#F4F4F4",
    ...(isDark ? { color: "#FFF" } : {}),
  };

  const darkLabel = isDark ? { "& label.MuiInputLabel-root": { color: "#C4C4C4" } } : {};

  return (
    <FormikProvider value={formik}>
      <Form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        onSubmitCapture={(e) => {
          if (domainRemainder) acceptSuggestion(e, true);
        }}
      >
        <Stack>
          <Stack
            component={motion.div}
            initial={{ opacity: 0, y: 60 }}
            animate={animate}
            direction={{ xs: "column", sm: "row" }}
          >
            <TextField
              fullWidth
              label="Name"
              autoFocus
              {...getFieldProps("fullName")}
              error={Boolean(touched.fullName && errors.fullName)}
              helperText={touched.fullName && errors.fullName}
              InputProps={{ style: inputStyle }}
              sx={{
                marginTop: "20px",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                ...darkLabel,
              }}
            />
          </Stack>

          <Stack component={motion.div} initial={{ opacity: 0, y: 40 }} animate={animate}>
            <Box sx={{ position: "relative", mt: "20px" }}>
              <TextField
                name="email"
                fullWidth
                autoComplete="username"
                type="email"
                label="Email address"
                value={values.email}
                onChange={handleEmailChange}
                onKeyDown={handleEmailKeyDown}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                inputProps={{ spellCheck: false, autoCapitalize: "none", autoCorrect: "off" }}
                InputProps={{ style: inputStyle }}
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                  "& .MuiOutlinedInput-input": { padding: "16.5px 14px", ...textMetrics },
                  ...darkLabel,
                }}
              />

              {!!domainRemainder && (
                <Box
                  onMouseDown={(e) => acceptSuggestion(e)}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    padding: "16.5px 14px",
                    pointerEvents: "auto",
                    userSelect: "none",
                    color: isDark ? "#AAAAAA" : "#9aa0a6",
                    whiteSpace: "pre",
                    ...textMetrics,
                  }}
                >
                  <span style={{ visibility: "hidden" }}>{values.email}</span>
                  <span style={{ display: "inline-block", transform: "translateY(0.5px)" }}>
                    {domainRemainder}
                  </span>
                </Box>
              )}
            </Box>
          </Stack>

          <Box component={motion.div} initial={{ opacity: 0, y: 20 }} animate={animate}>
            <LoadingButton
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
              sx={{
                marginTop: "20px",
                marginBottom: "3px",
                backgroundColor: isDark ? "#095E39" : "#00C56E",
                boxShadow: "none",
                borderRadius: "25px",
                height: "56px",
                "&:hover": {
                  backgroundColor: isDark ? "rgba(0, 197, 110, 0.1)" : "#00B864",
                  boxShadow: "none",
                },
              }}
            >
              Sign up
            </LoadingButton>
          </Box>
        </Stack>
      </Form>
    </FormikProvider>
  );
};

const SignupFormWithRecaptcha = (props) => (
  <GoogleReCaptchaProvider
    reCaptchaKey={AmplifyEnv.G_RECAPTCHA_V3_SITE_KEY}
    scriptProps={{ async: true, defer: true, appendTo: "head" }}
  >
    <SignupForm {...props} />
  </GoogleReCaptchaProvider>
);

export default SignupFormWithRecaptcha;